import { useCallback } from 'react';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { requestAccess } from '../../services/access-request/index.tsx';
import { fireErrorAnalytics } from '../../services/analytics/index.tsx';
import { useSnooze } from '../../services/caching/index.tsx';
import { useFilteredUsersFromCollaborationGraph } from '../../services/get-collaborators/index.tsx';
import { useNudgeStateJiraToLoom } from '../../services/nudge-state/index.tsx';
import { useRequestAccessErrorFlag } from '../request-access-error-flag/index.tsx';
import {
	getAnalyticsAttributes,
	getLoomCJNMessageStrings,
	useRequestAccessPromptFlag,
} from '../request-access-prompt-flag/index.tsx';
import { useRequestAccessSuccessFlag } from '../request-access-success-flag/index.tsx';
import { useFireLoomCjnFeatureExposedEventOnce } from '../eligibility/loom-eligibility.tsx';
import { MAX_FEATURE_VISIT_COUNT } from './common.tsx';

const MAX_NUMBER_OF_LOOM_COLLABORATORS = 10;

const openLoomInANewTab = () => {
	if (typeof globalThis === 'undefined') return;
	const newTab = globalThis.window?.open('https://www.loom.com/spaces', '_blank');
	if (newTab) {
		newTab.focus();
	}
};

export const useLoomCrossJoinNudge = () => {
	const cloudId = useCloudId();
	const userId = useAccountId() || '';
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const getFilteredCollaborators = useFilteredUsersFromCollaborationGraph(
		cloudId,
		'loom',
		MAX_NUMBER_OF_LOOM_COLLABORATORS,
	);
	const showRequestAccessPromptFlag = useRequestAccessPromptFlag();
	const showSuccessFlag = useRequestAccessSuccessFlag();
	const showErrorFlag = useRequestAccessErrorFlag();
	const fireLoomCjnFeatureExposedEventOnce = useFireLoomCjnFeatureExposedEventOnce();

	const { snoozeFor7Days, snoozeFor30Days, getVisitedCount, increaseVisitedCount } = useSnooze(
		'crossJoinNudgesJiraToLoom',
	);
	const { getNudgeState, setNudgeDismissed } = useNudgeStateJiraToLoom();

	return useCallback(async () => {
		try {
			const startTimestamp = Date.now();
			const onSubmit = async () => {
				try {
					const accessStatus = await requestAccess(
						'loom',
						cloudId,
						userId,
						'crossJoinNudgesJiraToLoom',
						createAnalyticsEvent,
					);
					if (
						accessStatus === 'ACCESS_GRANTED' ||
						accessStatus === 'ACCESS_EXISTS' ||
						accessStatus === 'APPROVED_REQUEST_EXISTS'
					) {
						// This is an exception as the eligibilty check fails when user has access to loom
						openLoomInANewTab();
					} else if (
						accessStatus === 'PENDING_REQUEST_CREATED' ||
						accessStatus === 'PENDING_REQUEST_EXISTS'
					) {
						showSuccessFlag();
					} else {
						showErrorFlag('loom');
					}
				} catch {
					showErrorFlag('loom');
				}
			};

			const isUserOptOut = (await getNudgeState()) === 'DISMISSED';
			const featureVisitedCount = await getVisitedCount();
			if (isUserOptOut || featureVisitedCount >= MAX_FEATURE_VISIT_COUNT) return;
			fireLoomCjnFeatureExposedEventOnce({
				cohort: expVal('twcg-442-cjn-jira-loom', 'cohort', 'not-enrolled'),
				flagKey: 'twcg-442-cjn-jira-loom',
			});

			// These snooze functions are for the case when user does not interact with the nudge
			if (featureVisitedCount === 0) {
				snoozeFor7Days();
			} else if (featureVisitedCount > 0) {
				snoozeFor30Days();
			}

			const collaboratorsOnLoom = await getFilteredCollaborators();
			if (expValEquals('twcg-442-cjn-jira-loom', 'cohort', 'variation', 'not-enrolled')) {
				showRequestAccessPromptFlag({
					users: collaboratorsOnLoom,
					onSubmit,
					onOptout: setNudgeDismissed,
					snoozeFor7Days,
					snoozeFor30Days,
					integration: 'crossJoinNudgesJiraToLoom',
					featureVisitedCount,
					messageParser: getLoomCJNMessageStrings({
						collaborators: collaboratorsOnLoom,
					}),
					eventAttributes: getAnalyticsAttributes({
						userId,
						users: collaboratorsOnLoom,
						featureVisitedCount,
						startTimestamp,
					}),
				});

				increaseVisitedCount(featureVisitedCount + 1);
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics('crossJoinNudgesJiraToLoom', error, {
				cloudId,
				jiraToLoomCrossJoinNudgesErrorType: 'nudgeViewedFailed',
			});
		}
	}, [
		cloudId,
		createAnalyticsEvent,
		getFilteredCollaborators,
		getNudgeState,
		getVisitedCount,
		increaseVisitedCount,
		setNudgeDismissed,
		showErrorFlag,
		showRequestAccessPromptFlag,
		showSuccessFlag,
		snoozeFor7Days,
		snoozeFor30Days,
		userId,
		fireLoomCjnFeatureExposedEventOnce,
	]);
};
